import { EDITOR_EFFECT_PRESETS, isProgressPreset, isTextEffectPreset } from './playable-editor.contract.js';
/** Hard cap on elements one AI generation may return — a named constant so the api and the schema agree. */
export const MAX_AI_ELEMENTS = 20;
const MAX_TEXT_LEN = 200;
const EFFECT_MS_MIN = 1;
const EFFECT_MS_MAX = 60000;
const MIN_ELEMENT_SIZE = 8;
const ELEMENT_TYPES = new Set(['text', 'box', 'button', 'progress', 'image']);
/** Editor colour inputs are hex; restricting AI colours to hex is fail-closed against a
 *  `background: "red;}</style>"` breakout that would otherwise surface as a save-time css error. */
const HEX_RE = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;
const FONT_WEIGHTS = new Set(['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900']);
const TEXT_ALIGNS = new Set(['left', 'center', 'right']);
const TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4']);
const COLOUR_KEYS = ['color', 'background', 'borderColor', 'fillColor'];
const NUMBER_KEYS = [
    'fontSize', 'borderRadius', 'borderWidth', 'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'radiusTopLeft', 'radiusTopRight', 'radiusBottomRight', 'radiusBottomLeft',
    'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'lineHeight',
];
/** 0..1 fractions. */
const UNIT_KEYS = ['opacity', 'borderOpacity', 'backgroundOpacity'];
/** Signed numbers with their own bounds. */
const SIGNED_KEYS = { rotate: [-360, 360], letterSpacing: [-50, 100] };
const BOOL_KEYS = ['borderHidden', 'backgroundHidden', 'flipX', 'flipY'];
const BORDER_POSITIONS = new Set(['inside', 'center', 'outside']);
const VERTICAL_ALIGNS = new Set(['top', 'middle', 'bottom']);
const ALLOWED_STYLE_KEYS = new Set([
    ...COLOUR_KEYS, ...NUMBER_KEYS, ...UNIT_KEYS, ...Object.keys(SIGNED_KEYS), ...BOOL_KEYS,
    'fontWeight', 'textAlign', 'tag', 'borderPosition', 'verticalAlign',
]);
export const PLAYABLE_TRANSLATE_MAX_LANGS = 8;
export const PLAYABLE_TRANSLATE_MAX_TEXTS = 150;
export const PLAYABLE_TRANSLATE_MAX_TEXT_CHARS = 500;
export const PLAYABLE_TRANSLATE_MAX_TOTAL_CHARS = 6000;
/** BCP-47-ish: a primary subtag and optional subtags (`vi`, `pt-BR`, `zh-TW`). */
export const PLAYABLE_LANG_CODE_RE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
const TRANSLATE_ID_RE = /^[A-Za-z0-9_-]{1,40}$/;
/** Returns the reason a translate request is refused, or null when it is within every cap. */
export function translateRequestError(body) {
    const b = body;
    if (!b || !Array.isArray(b.langs) || b.langs.length === 0)
        return 'langs must be a non-empty array';
    if (b.langs.length > PLAYABLE_TRANSLATE_MAX_LANGS)
        return `at most ${PLAYABLE_TRANSLATE_MAX_LANGS} languages per call`;
    for (const lang of b.langs) {
        if (typeof lang !== 'string' || !PLAYABLE_LANG_CODE_RE.test(lang))
            return `invalid language code '${String(lang)}'`;
    }
    if (!b.texts || typeof b.texts !== 'object' || Array.isArray(b.texts))
        return 'texts must be an object';
    const entries = Object.entries(b.texts);
    if (entries.length === 0)
        return 'texts must not be empty';
    if (entries.length > PLAYABLE_TRANSLATE_MAX_TEXTS)
        return `at most ${PLAYABLE_TRANSLATE_MAX_TEXTS} texts per call`;
    let total = 0;
    for (const [id, text] of entries) {
        if (!TRANSLATE_ID_RE.test(id))
            return `invalid text id '${id}'`;
        if (typeof text !== 'string')
            return `text '${id}' must be a string`;
        if (text.length > PLAYABLE_TRANSLATE_MAX_TEXT_CHARS)
            return `text '${id}' exceeds ${PLAYABLE_TRANSLATE_MAX_TEXT_CHARS} chars`;
        total += text.length;
    }
    if (total > PLAYABLE_TRANSLATE_MAX_TOTAL_CHARS)
        return `texts exceed ${PLAYABLE_TRANSLATE_MAX_TOTAL_CHARS} chars in total`;
    return null;
}
/**
 * Keeps only what was asked for from the model's JSON: requested languages, requested ids, string
 * values within the per-text cap. Everything else is dropped rather than trusted, and an id the model
 * left out is simply absent — the caller treats it as untranslated.
 */
export function cleanTranslateOutput(raw, langs, ids) {
    const values = {};
    const byLang = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const wanted = new Set(ids);
    for (const lang of langs) {
        const set = byLang[lang];
        const out = {};
        if (set && typeof set === 'object' && !Array.isArray(set)) {
            for (const [id, text] of Object.entries(set)) {
                if (!wanted.has(id) || typeof text !== 'string')
                    continue;
                const trimmed = text.trim();
                if (trimmed && trimmed.length <= PLAYABLE_TRANSLATE_MAX_TEXT_CHARS * 2)
                    out[id] = trimmed;
            }
        }
        values[lang] = out;
    }
    return { values };
}
function clamp(v, min, max) {
    return Math.min(Math.max(v, min), Math.max(min, max));
}
/** Validates one AI-proposed element's style; returns the cleaned style or an error string (structural
 *  violation → reject the whole response). Pushes a warning per clamped/dropped value. */
function cleanStyle(raw, warnings, where) {
    if (raw == null)
        return {};
    if (typeof raw !== 'object')
        return `${where}: style must be an object`;
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        if (!ALLOWED_STYLE_KEYS.has(key))
            return `${where}: unknown style key '${key}'`;
        if (COLOUR_KEYS.includes(key)) {
            if (typeof value !== 'string' || !HEX_RE.test(value))
                return `${where}: style.${key} must be a #hex colour`;
            out[key] = value;
        }
        else if (NUMBER_KEYS.includes(key)) {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return `${where}: style.${key} must be a number`;
            out[key] = clamp(Math.round(value), 0, 2000);
        }
        else if (UNIT_KEYS.includes(key)) {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return `${where}: style.${key} must be a number`;
            out[key] = clamp(value, 0, 1);
        }
        else if (key in SIGNED_KEYS) {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return `${where}: style.${key} must be a number`;
            const [lo, hi] = SIGNED_KEYS[key];
            out[key] = clamp(Math.round(value), lo, hi);
        }
        else if (BOOL_KEYS.includes(key)) {
            if (typeof value !== 'boolean')
                return `${where}: style.${key} must be a boolean`;
            out[key] = value;
        }
        else if (key === 'borderPosition') {
            if (typeof value !== 'string' || !BORDER_POSITIONS.has(value))
                return `${where}: style.borderPosition is invalid`;
            out.borderPosition = value;
        }
        else if (key === 'verticalAlign') {
            if (typeof value !== 'string' || !VERTICAL_ALIGNS.has(value))
                return `${where}: style.verticalAlign is invalid`;
            out.verticalAlign = value;
        }
        else if (key === 'fontWeight') {
            const s = String(value);
            if (!FONT_WEIGHTS.has(s))
                return `${where}: style.fontWeight '${s}' is not allowed`;
            out.fontWeight = s;
        }
        else if (key === 'textAlign') {
            if (typeof value !== 'string' || !TEXT_ALIGNS.has(value))
                return `${where}: style.textAlign is invalid`;
            out.textAlign = value;
        }
        else if (key === 'tag') {
            if (typeof value !== 'string' || !TAGS.has(value))
                return `${where}: style.tag is invalid`;
            out.tag = value;
        }
    }
    return out;
}
/** Validates + normalizes AI element JSON against the editor contract, re-minting ids and stripping the
 *  untrusted fields. `raw` may be an Element[] or a `{ elements: [...] }` wrapper. Fail-closed. */
export function validateAiElements(raw, canvas) {
    const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.elements) ? raw.elements : null;
    if (!arr)
        return { ok: false, error: 'expected an array of elements' };
    if (arr.length === 0)
        return { ok: false, error: 'no elements returned' };
    if (arr.length > MAX_AI_ELEMENTS)
        return { ok: false, error: `too many elements (${arr.length} > ${MAX_AI_ELEMENTS})` };
    const warnings = [];
    const prefix = `ai${Math.random().toString(36).slice(2, 7)}`;
    const elements = [];
    for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        const where = `element ${i + 1}`;
        if (!e || typeof e !== 'object')
            return { ok: false, error: `${where}: not an object` };
        const el = e;
        if (typeof el.type !== 'string' || !ELEMENT_TYPES.has(el.type)) {
            return { ok: false, error: `${where}: unknown type '${String(el.type)}'` };
        }
        const type = el.type;
        for (const k of ['x', 'y', 'w', 'h']) {
            if (typeof el[k] !== 'number' || !Number.isFinite(el[k]))
                return { ok: false, error: `${where}: ${k} must be a number` };
        }
        const style = cleanStyle(el.style, warnings, where);
        if (typeof style === 'string')
            return { ok: false, error: style };
        const w = clamp(Math.round(el.w), MIN_ELEMENT_SIZE, canvas.w);
        const h = clamp(Math.round(el.h), MIN_ELEMENT_SIZE, canvas.h);
        const x = clamp(Math.round(el.x), 0, Math.max(0, canvas.w - w));
        const y = clamp(Math.round(el.y), 0, Math.max(0, canvas.h - h));
        if (x !== el.x || y !== el.y || w !== el.w || h !== el.h)
            warnings.push(`${where}: geometry clamped into the canvas`);
        const out = { id: `${prefix}-${i + 1}`, type, x, y, w, h, style };
        if ((type === 'text' || type === 'button') && el.text != null) {
            let text = String(el.text);
            if (text.length > MAX_TEXT_LEN) {
                text = text.slice(0, MAX_TEXT_LEN);
                warnings.push(`${where}: text truncated to ${MAX_TEXT_LEN} chars`);
            }
            out.text = text;
        }
        if (el.effect != null) {
            const fx = el.effect;
            if (typeof fx.preset !== 'string' || !EDITOR_EFFECT_PRESETS.includes(fx.preset)) {
                return { ok: false, error: `${where}: effect.preset '${String(fx.preset)}' is not a known preset` };
            }
            // Both families throw in the serializer when they land on the wrong kind of element, so an AI
            // layout that misplaces one would make the whole screen unsavable: drop the effect, keep the box.
            if (isProgressPreset(fx.preset) && type !== 'progress') {
                warnings.push(`${where}: ${fx.preset} effect dropped (not a progress element)`);
            }
            else if (isTextEffectPreset(fx.preset) && type !== 'text' && type !== 'button') {
                warnings.push(`${where}: ${fx.preset} effect dropped (not a text element)`);
            }
            else {
                const durationMs = clamp(Math.round(Number(fx.durationMs) || 0) || 500, EFFECT_MS_MIN, EFFECT_MS_MAX);
                const delayMs = clamp(Math.round(Number(fx.delayMs) || 0), 0, EFFECT_MS_MAX);
                out.effect = { preset: fx.preset, durationMs, delayMs };
            }
        }
        // imageAssetName / advanceTrigger / imageDataBase64 / id are deliberately NOT copied from AI input.
        elements.push(out);
    }
    return { ok: true, elements, warnings };
}
//# sourceMappingURL=playable-ai.contract.js.map